/**
 * BlockLoader v2 — loads v2 block files (meta.json, state.json, data.json, ui.json, events.json, flow.json).
 */

import type { BlockDef, BlockMeta, UiTree, EventMap, FlowMap, BlockState, DataMap } from "@cogo/types-v2"

/** v2 file name convention */
export const V2_FILES = {
  meta: "meta.json",
  ui: "ui.json",
  events: "events.json",
  flow: "flow.json",
  state: "state.json",
  data: "data.json",
} as const

export type FileReader = (path: string) => Promise<unknown>

/**
 * Load a v2 block from its directory.
 * @param basePath - Block directory path
 * @param readFile - Async file reader function (injected for platform independence)
 */
export async function loadBlock(basePath: string, readFile: FileReader): Promise<BlockDef> {
  const normalize = (p: string) => p.endsWith("/") ? p : p + "/"
  const base = normalize(basePath)

  const [meta, ui, events, flow, state, data] = await Promise.all([
    readFile(base + V2_FILES.meta).catch(() => null) as Promise<BlockMeta | null>,
    readFile(base + V2_FILES.ui).catch(() => ({})) as Promise<UiTree>,
    readFile(base + V2_FILES.events).catch(() => ({})) as Promise<EventMap>,
    readFile(base + V2_FILES.flow).catch(() => ({})) as Promise<FlowMap>,
    readFile(base + V2_FILES.state).catch(() => ({})) as Promise<BlockState>,
    readFile(base + V2_FILES.data).catch(() => ({})) as Promise<DataMap>,
  ])

  if (!meta) {
    throw new Error(`[block-loader-v2] meta.json not found in ${basePath}`)
  }

  if (!meta.stateContract) {
    meta.stateContract = { localState: {}, inputSymbols: [], outputSymbols: [] }
  }

  return { meta, ui, events, flow, state, data }
}

/**
 * Detect whether a block directory is v2 format (has meta.json).
 */
export async function isV2Block(basePath: string, readFile: FileReader): Promise<boolean> {
  try {
    const base = basePath.endsWith("/") ? basePath : basePath + "/"
    const meta = await readFile(base + V2_FILES.meta) as Record<string, unknown> | null
    return meta != null && typeof meta === "object"
  } catch {
    return false
  }
}
